import { Component, h, Event, EventEmitter, Listen, Host } from '@stencil/core';
import spacetime from 'spacetime';

import { formatDuration, formatOrderDate } from '../../utils/utils';
import { addStyleSettings } from '../../utils/custom-style';
import { getIncrement } from '../../utils/calendar';
import { getPriceBreakdown } from '../../utils/order-price';

import { selection } from '../../store/selection.store';
import { settings } from '../../store/settings.store';
import { schedule } from '../../store/schedule.store';

import { SpaceOptionValue, SpaceOption } from '@artask/tambr-models/models/space-options.model';



@Component({
  tag: 'tambr-calendar-selection',
  styleUrl: 'tambr-calendar-selection.css',
  shadow: true
})
export class TambrCalendarSelection {

  @Event() tambrCalendarSelectionCancel: EventEmitter;
  @Event() tambrCalendarSelectionCheckout: EventEmitter;

  @Listen('click') onClickEvent( ev: MouseEvent ) {
    const targets = ev.composedPath() as EventTarget[];
    if (targets[0] instanceof Element && targets[0].id === 'tambr-calendar-selection') {
      return this.close();
    }
  }
  
  tz = settings.businessSettings.timezone;
  scheduleSettings = settings.businessSettings.scheduleSettings;
  scheduleIncrement = this.scheduleSettings.scheduleIncrement;
  slotDuration = this.scheduleSettings.slotDuration;
  incrementsPerSlot = this.slotDuration / this.scheduleIncrement;

  componentWillLoad() {
    this.selectFirstAvailableSpaceOptionValues();
  }

  checkout() { 
    this.tambrCalendarSelectionCheckout.emit();
  }

  close() { 
    this.tambrCalendarSelectionCancel.emit();
  }



  /**
   * Duration
   */

  decreaseDuration() { 
    if ( this.isDurationAtMin() ) return;
    selection.increments = selection.increments.slice( 0, -1 * this.incrementsPerSlot );
    this.setSelectedDuration();
  }

  isDurationAtMin() {
    const bookingDurationMin = this.scheduleSettings?.bookingDurationMin;
    const minValue = bookingDurationMin.enabled ? bookingDurationMin.value : 1;
    return selection.increments.length === minValue * this.incrementsPerSlot;
  }
    
  isDurationAtMax() {
    const maxEnabled = this.scheduleSettings?.bookingDurationMax?.enabled;
    const maxValue = this.scheduleSettings.bookingDurationMax.value;
    return maxEnabled && selection.increments.length >= maxValue * this.incrementsPerSlot;
  }

  increaseDuration() {
    if ( this.isDurationAtMax() ) return;
    const increments = this.getScheduleIncrementsForSlot( selection.spaceId, this.getNextSlotTime() );
    if ( !increments ) return;
    selection.increments = [ ...selection.increments, ...increments ];
    this.setSelectedDuration();
  }

  setSelectedDuration() {
    selection.duration = selection.increments.length  * this.scheduleIncrement;
  }


  getNextSlotTime(): number { 
    return selection.increments[ selection.increments.length-1 ] + this.scheduleIncrement * 60 * 1000;
  }

  isNextSlotAvailable(): boolean {
    return !!this.getScheduleIncrementsForSlot( selection.spaceId, this.getNextSlotTime() );
  }

  getScheduleIncrementsForSlot( spaceId: string, slotTimestamp: number ): number[] {
    const increments = [];
    for ( let i = 0; i < this.incrementsPerSlot; i++ ) {
      const checkedIncrementTimestamp = slotTimestamp + ( i * this.scheduleIncrement * 60 * 1000 );
      const checkedIncrement = getIncrement( spaceId, checkedIncrementTimestamp );
      if ( !checkedIncrement?.available ) return null;
      increments.push( checkedIncrementTimestamp );
    }
    return increments;
  }



  /**
   * Space options
   */

  getSelectedSpaceAvailableOptions(): SpaceOption[] {
    if ( !schedule.dateSchedule?.spaceOptions ) return [];
    const selectedSpace = schedule.dateSchedule.spaces.find( space => space.id === selection.spaceId );
    if ( !selectedSpace.options ) return [];
    return schedule.dateSchedule.spaceOptions.filter( s => selectedSpace.options.includes( s.id ) );
  }

  selectFirstAvailableSpaceOptionValues() {
    selection.spaceOptions = this.getSelectedSpaceAvailableOptions().map( option => {
      const firstValue = option.values[0];
      return {
        optionName: option.name, 
        optionId: option.id, 
        valueName: firstValue.name, 
        amount: firstValue.amount, 
        type: firstValue.type, 
        round: firstValue.round
      };
    });
  }

  handleOptionSelect( optionId: string, optionName: string, value: SpaceOptionValue ) {
    const selectedOptionIndex = selection.spaceOptions.findIndex( option => option.optionId === optionId );
    const selectedSpaceOptions = [ ...selection.spaceOptions ];
    selectedSpaceOptions[ selectedOptionIndex ] = { 
      optionId, 
      optionName, 
      valueName: value.name, 
      amount: value.amount, 
      round: value.round, 
      type: value.type
    };
    selection.spaceOptions = selectedSpaceOptions;
  }

  isSpaceOptionValueChecked( optionId: string, valueName: string ): boolean {
    const selectedOptionIndex = selection.spaceOptions.findIndex( option => option.optionId === optionId );
    return selection.spaceOptions[ selectedOptionIndex ].valueName === valueName;
  }



  render() {
    return (
      <Host style={ addStyleSettings() }>
        <div id="tambr-calendar-selection" class="tambr-overlay">          
          <div class="tambr-modal-window calendar-selection-window">    
            
            <div class="tambr-modal-header">
              <tambr-icon type="close" class="close-button" onClick={ () => this.close() } />
            </div>

            <div class="tambr-modal-body">
              <div class="tambr-modal-title">Your order</div>
              <div class="order-details">
                { this.renderDetailRow( 'Location', schedule.dateSchedule.calendar[ selection.spaceId ].locationTitle, 'bold-value' ) }
                { this.renderDetailRow( 'Space', schedule.dateSchedule.calendar[ selection.spaceId ].title, 'bold-value' ) }
                { this.renderDetailRow( 'Date', formatOrderDate( selection.date ) ) }
                { this.renderDetailRow( 'Start time', spacetime( selection.increments[0], this.tz ).format('time') ) }
                { this.renderSelectionDuration() } 
                { this.renderSpaceOptions() }
              </div>             
              <tambr-checkout-order-price priceBreakdown={ getPriceBreakdown().priceBreakdown } />
            </div>
            
            <div class="tambr-action-buttons row">
              <div class="tambr-clear-button" onClick={ () => this.close() }>Cancel</div>
              <button class='tambr-button'
                disabled={ !settings.businessSettings?.stripeAccount } 
                onClick={ () => this.checkout() }>Checkout</button>
            </div>

          </div>          
        </div>
      </Host>   
    );
  }

  renderDetailRow( label: string, value: string, cssClass: string = '' ) {
    if ( !value ) return;
    return (
      <div class={ "order-details-row " + cssClass }>
        <div class="order-details-label">{ label }</div>
        <div class="order-details-value">{ value }</div>
      </div>
    );
  }

  renderSelectionDuration() {
    return (
      <div class="order-details-row">
        <div class="order-details-label">Duration</div>
        <div class="order-details-value">
          <div class="duration">
            <div class={ 'duration-button ' + (this.isDurationAtMin() ? 'disabled' : 'active') } 
              onClick={ () => this.decreaseDuration() }>
                <tambr-icon type='minus'></tambr-icon>
            </div>

            <div class={ 'duration-value ' + ( this.slotDuration === 60 ? '' : 'long' ) }>
              { formatDuration( selection.duration ) }
            </div>

            <div class={ 'duration-button ' + (this.isNextSlotAvailable() && !this.isDurationAtMax() ? 'active' : 'disabled') } 
              onClick={ () => this.increaseDuration() }>
                <tambr-icon type='plus'></tambr-icon>
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderSpaceOptions() {
    return this.getSelectedSpaceAvailableOptions().map( option => 
      <div class="order-details-row">
        <div class="order-details-label">{ option.name }</div>
        <div class="order-details-value">
          <div class="values-wrapper">
            { option.values.map( value => [
              <input class='value-button' type="radio" id={ value.name }  
                onClick={ () => this.handleOptionSelect( option.id, option.name, value ) } 
                checked={ this.isSpaceOptionValueChecked( option.id, value.name ) } />,
              <label htmlFor={ value.name }>{ value.name }</label>
            ] ) }
          </div>
        </div>
      </div>
    );
  }

}
